import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function AdminVerifyPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const ref0 = useRef();
  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();
  const ref4 = useRef();
  const ref5 = useRef();
  const inputRefs = [ref0, ref1, ref2, ref3, ref4, ref5];

  // Get member_id from localStorage (set during login redirect) or URL param
  const getMemberId = () => {
    return localStorage.getItem('vedanta_pending_2fa_member_id') || searchParams.get('member_id');
  };

  const memberId = getMemberId();

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, []);

  // Cooldown timer effect - only runs when cooldown actually changes to > 0
  useEffect(() => {
    if (cooldown <= 0) return;
    
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleInputChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
    setCode(prev => {
      const newCode = [...prev.split('')];
      newCode[index] = value;
      return newCode.join('');
    });
  };

  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace' && e.target.value === '' && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    if (e.key === 'Backspace' && e.target.value === '') {
      setCode(prev => {
        const newCode = [...prev.split('')];
        newCode[index] = '';
        return newCode.join('');
      });
    }
  };

  const handleResendCode = async () => {
    if (cooldown > 0 || !memberId) return;
    
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      await api.post('/api/auth/admin-resend', { member_id: memberId });
      setCooldown(60);
      setMessage('A new verification code has been sent to your email.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    if (code.length !== 6) {
      setError('Please enter the full 6-digit verification code.');
      return;
    }

    setLoading(true);

    try {
      const res = await api.post('/api/auth/admin-verify', {
        member_id: memberId,
        code,
      });
      
      if (res.data.requires_2fa) {
        setError('Invalid verification code.');
        setCode('');
        inputRefs[0].current?.focus();
      } else {
        await login(res.data.access_token);
        localStorage.removeItem('vedanta_pending_2fa_member_id');
        navigate('/admin');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes('too many failed')) {
        setError('Account disabled due to too many failed verification attempts.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Invalid or expired verification code.');
      }
      setCooldown(30);
      setCode('');
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!memberId) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card admin-verify-card">
        <h2>Admin Verification</h2>
        <p className="verify-instructions">
          Enter the 6-digit verification code sent to your email.
        </p>
        
        {error && <p className="form-error">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        
        <form onSubmit={handleSubmit} className="verify-form">
          <div className="code-input-row">
            {[...Array(6)].map((_, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={code[i] || ''}
                onChange={(e) => handleInputChange(e, i)}
                onKeyDown={(e) => handleBackspace(e, i)}
                className="code-input"
                autoComplete="one-time-code"
                aria-label={`Verification digit ${i + 1}`}
                required
              />
            ))}
          </div>
          
          <button type="submit" disabled={loading || code.length !== 6}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
        
        <div className="verify-footer">
          <button 
            onClick={handleResendCode} 
            disabled={cooldown > 0 || loading}
            className="resend-btn"
          >
            Resend Code
            {cooldown > 0 && (
              <span className="cooldown-timer"> ({cooldown}s)</span>
            )}
          </button>
          <button 
            type="button" 
            className="auth-link-btn" 
            onClick={() => navigate('/account')}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
